import {
  loadMultipleShopifyCollectionsByHandles,
  loadShopifyCollectionByHandle
} from '@portal/api/shopifyV2/collection';
import { FormattedShopifyCollection } from '@portal/types/shopify/product.formatted.types';
import { StoreCode } from '@portal/schema/storeCode';
import { useStoreValue } from '@portal/state/hooks/useStore';
import { useQuery } from '@tanstack/react-query';
import { Identifier } from '@portal/types/shopify/request.types';

interface GetSingleBundlesCollectionQueryProps {
  storeCode: StoreCode;
  collectionHandle: string;
  isEnabled?: boolean;
  identifiers?: Identifier[];
}

interface GetBundlesCollectionQueryProps {
  storeCode: StoreCode;
  collectionHandles: string[];
  isEnabled?: boolean;
  identifiers?: Identifier[];
}

export function useShopListBundleCollectionQuery({
  storeCode,
  collectionHandle,
  identifiers,
  isEnabled
}: GetSingleBundlesCollectionQueryProps) {
  return useQuery({
    queryKey: ['shop-list-bundles', storeCode, collectionHandle, identifiers],
    queryFn: () => loadShopifyCollectionByHandle(storeCode, collectionHandle, identifiers),
    enabled: isEnabled
  });
}

export function useShopListBundlesCollectionsQuery({
  storeCode,
  collectionHandles,
  identifiers
}: GetBundlesCollectionQueryProps) {
  return useQuery({
    queryKey: ['shop-list-bundles', storeCode, collectionHandles, identifiers],
    queryFn: () =>
      loadMultipleShopifyCollectionsByHandles(storeCode, collectionHandles, identifiers),
    enabled: true
  });
}

export function useShopListBundlesCollections(
  collectionHandles: string[],
  identifiers?: Identifier[]
) {
  const store = useStoreValue();

  const { isPending, data: collections } = useShopListBundlesCollectionsQuery({
    storeCode: store,
    collectionHandles,
    ...(identifiers && { identifiers })
  });

  // remove collections that were not found by the collection handle
  const formattedCollections = (collections?.filter(
    (collection) => collection !== undefined && collection !== null
  ) || []) as FormattedShopifyCollection[];

  return {
    isPending,
    data: formattedCollections
  };
}
